import { z } from 'zod';
import { DomainError } from '../errors.js';
import type { ParseResult } from '../voyage/schemas.js';
import type { PdiCheckpointContent } from './library-data.js';
import { parsed, requiredParagraph, requiredText, typed } from './schema.js';

/**
 * Pre-Docking Inspection kütüphanesi (ADR 0129 §3; ortağın Excel'i "Data & Workflow Rules"). Ağaç
 * üç düzeydir: alan (AREA) → sistem (SYSTEM) → kontrol noktası (CHECKPOINT). Yalnız kontrol noktası
 * denetlenir; alan ve sistem gezinme düğümüdür. Kod düzeye bağlıdır, değişmez ve hiç yeniden
 * kullanılmaz (Excel "ID stability"). Kütüphane şirket verisidir: ofiste yazılır, yayımlanan sürüm
 * (tam ağaç kopyası) değiştirilemez ve denetim örneği onu dondurur.
 */

export type { PdiCheckpointContent } from './library-data.js';

export const PDI_LEVELS = ['area', 'system', 'checkpoint'] as const;
export type PdiLevel = (typeof PDI_LEVELS)[number];

const PATTERNS: Readonly<Record<PdiLevel, RegExp>> = {
  area: /^PDI-\d{2}$/,
  system: /^PDI-\d{2}-\d{2}$/,
  checkpoint: /^PDI-\d{2}-\d{2}-\d{3}$/,
};

/** Kodun düzeyi; biçim dışı kodda `null`. */
export function pdiLevelOf(code: string): PdiLevel | null {
  for (const level of PDI_LEVELS) if (PATTERNS[level].test(code)) return level;
  return null;
}

export function isPdiCode(code: string): boolean {
  return pdiLevelOf(code) !== null;
}

/** Üst düğümün kodu (kodun öneki); alanda `null`. */
export function pdiParentCode(code: string): string | null {
  const level = pdiLevelOf(code);
  if (level === null || level === 'area') return null;
  return code.slice(0, code.lastIndexOf('-'));
}

/**
 * Üst düğümün altına açılacak yeni kodun sıradaki numarası (`null`: yeni alan). Şirkette bir kez
 * bulunmuş her kod (silinmiş ve pasif dahil) verilir; en büyüğünden sonraki numara döner, boşluk
 * doldurulmaz: yayımlanmış kimlik başka noktaya verilmez (Excel "ID stability").
 */
export function nextPdiCode(parentCode: string | null, everCodes: Iterable<string>): string {
  const parentLevel = parentCode === null ? null : pdiLevelOf(parentCode);
  if (parentCode !== null && (parentLevel === null || parentLevel === 'checkpoint')) {
    throw new DomainError('INVALID_PDI_NODE', `parent ${parentCode}`);
  }
  const prefix = parentCode === null ? 'PDI' : parentCode;
  const width = parentLevel === 'system' ? 3 : 2;
  const childLevel: PdiLevel =
    parentLevel === null ? 'area' : parentLevel === 'area' ? 'system' : 'checkpoint';
  // Bir kod üstlerinin de bir kez var olduğunu gösterir
  const all = new Set<string>();
  for (const code of everCodes) {
    for (let c: string | null = code; c !== null; c = pdiParentCode(c)) all.add(c);
  }
  let max = 0;
  for (const code of all) {
    if (pdiLevelOf(code) !== childLevel || !code.startsWith(`${prefix}-`)) continue;
    if (childLevel !== 'area' && pdiParentCode(code) !== parentCode) continue;
    max = Math.max(max, Number(code.slice(prefix.length + 1)));
  }
  const next = max + 1;
  if (next >= 10 ** width) throw new DomainError('PDI_CODE_EXHAUSTED', prefix);
  return `${prefix}-${String(next).padStart(width, '0')}`;
}

export const pdiCheckpointContentSchema = z.strictObject({
  scope: requiredParagraph(2000),
  method: requiredText(300),
  timing: requiredText(300),
  acceptance: requiredText(500),
});

export const pdiNodeSchema = z
  .strictObject({
    code: z
      .string(typed('INVALID_TEXT'))
      .transform((value) => value.trim().toUpperCase())
      .pipe(z.string().min(1, 'REQUIRED')),
    level: z.enum(PDI_LEVELS, typed('INVALID_OPTION')),
    parentCode: z
      .string(typed('INVALID_TEXT'))
      .transform((value) => value.trim().toUpperCase())
      .nullish()
      .transform((value) => (value ? value : null)),
    name: requiredText(200),
    sortOrder: z
      .number(typed('INVALID_NUMBER'))
      .int('INVALID_NUMBER')
      .min(0, 'OUT_OF_RANGE')
      .max(9999, 'OUT_OF_RANGE')
      .nullish()
      .transform((value) => value ?? 0),
    active: z
      .boolean(typed('INVALID_BOOLEAN'))
      .nullish()
      .transform((value) => value ?? true),
    content: z
      .unknown()
      .optional()
      .transform((value) => value ?? null),
  })
  .transform((value, ctx) => {
    if (pdiLevelOf(value.code) !== value.level) {
      ctx.addIssue({ code: 'custom', path: ['code'], message: 'INVALID_CODE' });
      return z.NEVER;
    }
    if (pdiParentCode(value.code) !== value.parentCode) {
      ctx.addIssue({ code: 'custom', path: ['parentCode'], message: 'PARENT_MISMATCH' });
      return z.NEVER;
    }
    if (value.level !== 'checkpoint') {
      if (value.content !== null) {
        ctx.addIssue({ code: 'custom', path: ['content'], message: 'NOT_ALLOWED' });
        return z.NEVER;
      }
      return { ...value, content: null };
    }
    if (value.content === null) {
      ctx.addIssue({ code: 'custom', path: ['content'], message: 'REQUIRED' });
      return z.NEVER;
    }
    const content = pdiCheckpointContentSchema.safeParse(value.content);
    if (!content.success) {
      for (const issue of content.error.issues) {
        ctx.addIssue({ code: 'custom', path: ['content', ...issue.path], message: issue.message });
      }
      return z.NEVER;
    }
    return { ...value, content: content.data as PdiCheckpointContent };
  });
export type PdiNodeInput = z.output<typeof pdiNodeSchema>;

export function parsePdiNode(input: unknown): ParseResult<PdiNodeInput> {
  return parsed(pdiNodeSchema.safeParse(input));
}

/** Sürüm kopyasındaki düğüm (değiştirilemez `pdi_library_versions.nodes`). */
export interface PdiSnapshotNode {
  code: string;
  level: PdiLevel;
  parentCode: string | null;
  name: string;
  sortOrder: number;
  content: PdiCheckpointContent | null;
}

export interface PdiNodeLike {
  code: string;
  level: string;
  parentCode: string | null;
  name: string;
  sortOrder: number;
  active: boolean;
  content: PdiCheckpointContent | null;
}

const byOrder = (a: { sortOrder: number; code: string }, b: { sortOrder: number; code: string }) =>
  a.sortOrder - b.sortOrder || a.code.localeCompare(b.code);

/**
 * Yayımlanacak ağaç: yalnız etkin düğümler ve etkin üstleri olanlar, ağaç sırasıyla (alan; altında
 * sistemleri; altında noktaları; her düzeyde sıra, sonra kod). Giriş sırası sonucu değiştirmez.
 */
export function pdiSnapshot(nodes: readonly PdiNodeLike[]): PdiSnapshotNode[] {
  const active = nodes.filter((n) => n.active);
  const children = new Map<string | null, PdiNodeLike[]>();
  for (const node of active) {
    const list = children.get(node.parentCode) ?? [];
    list.push(node);
    children.set(node.parentCode, list);
  }
  const out: PdiSnapshotNode[] = [];
  const walk = (parent: string | null, level: PdiLevel) => {
    for (const node of [...(children.get(parent) ?? [])].sort(byOrder)) {
      if (node.level !== level) continue;
      out.push({
        code: node.code,
        level,
        parentCode: node.parentCode,
        name: node.name,
        sortOrder: node.sortOrder,
        content: level === 'checkpoint' ? node.content : null,
      });
      if (level === 'area') walk(node.code, 'system');
      if (level === 'system') walk(node.code, 'checkpoint');
    }
  };
  walk(null, 'area');
  return out;
}

export function pdiCounts(snapshot: readonly PdiSnapshotNode[]) {
  return {
    areas: snapshot.filter((n) => n.level === 'area').length,
    systems: snapshot.filter((n) => n.level === 'system').length,
    checkpoints: snapshot.filter((n) => n.level === 'checkpoint').length,
  };
}

export interface PdiTreeCheckpoint {
  code: string;
  name: string;
  content: PdiCheckpointContent;
}
export interface PdiTreeSystem {
  code: string;
  name: string;
  checkpoints: PdiTreeCheckpoint[];
}
export interface PdiTreeArea {
  code: string;
  name: string;
  systems: PdiTreeSystem[];
}

/** Sürüm kopyasından gezinme ağacı (ilk ekran: 10 alan; alan → sistem → nokta). */
export function pdiTree(snapshot: readonly PdiSnapshotNode[]): PdiTreeArea[] {
  const areas: PdiTreeArea[] = [];
  const systems = new Map<string, PdiTreeSystem>();
  for (const node of snapshot) {
    if (node.level === 'area') {
      areas.push({ code: node.code, name: node.name, systems: [] });
    } else if (node.level === 'system') {
      const system = { code: node.code, name: node.name, checkpoints: [] };
      systems.set(node.code, system);
      areas.find((a) => a.code === node.parentCode)?.systems.push(system);
    } else if (node.content) {
      systems
        .get(node.parentCode ?? '')
        ?.checkpoints.push({ code: node.code, name: node.name, content: node.content });
    }
  }
  return areas;
}
